import React, { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";

// Security Insights → Frameworks: one bar per framework's readiness, in the bar style of the
// assessments' framework chart. Readiness is good when high, so the colours run the other way from
// the assessments' risk-exposure bars: green at 75% and above, red under 25%. Clicking a bar opens
// that framework's requirements. A framework with no percentage (unscorable, or nothing evidenced
// yet) has no bar: it is listed under the chart with its counts and opens the same way. The
// drop-down reaches every framework from the keyboard.

export const readinessColor = (pct) => {
    if (pct >= 75) return "#16a34a"; // green
    if (pct >= 50) return "#84cc16"; // lime
    if (pct >= 25) return "#f59e0b"; // amber
    return "#dc2626";                // red
};

// Bars for frameworks with a readiness percentage; the rest are listed beside the chart.
export const splitForChart = (estimates) => {
    const bars = [];
    const others = [];
    for (const e of estimates || []) {
        if (e.observedControls && e.readinessBasis !== "Unscorable")
            bars.push({ framework: e.framework, score: Math.round(e.adjustedCoveragePercent), estimate: e });
        else others.push(e);
    }
    return { bars, others };
};

const counts = (e) => `${e.passingControls} pass · ${e.partialControls} partial · ${e.failingControls} fail`;

// What a framework without a bar shows instead of a percentage.
export const noBarText = (e) => (e.observedControls ? counts(e) : "No evidence yet");

const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const { framework, score, estimate } = payload[0].payload;
    return (
        <div className="bg-white border border-gray-200 px-3 py-2 text-xs shadow-lg rounded">
            <div className="font-semibold text-gray-800">{framework}</div>
            <div className="text-gray-700">Readiness: {score}%</div>
            <div className="text-gray-500">{counts(estimate)}</div>
        </div>
    );
};

// The percentage above a bar: plain dark text, the same for every bar.
const BarLabel = ({ x, y, width, value }) => (
    <text x={Number(x) + Number(width) / 2} y={Number(y) - 6} textAnchor="middle"
        fontSize={12} fontWeight={500} fill="#374151" stroke="none">
        {`${value}%`}
    </text>
);

const LEGEND = [
    ["bg-green-600", "75% and above"],
    ["bg-lime-500", "50–74%"],
    ["bg-amber-500", "25–49%"],
    ["bg-red-600", "Under 25%"],
];

const FrameworkReadinessChart = ({ estimates, selected, onSelect, optionLabel }) => {
    const { bars, others } = useMemo(() => splitForChart(estimates), [estimates]);
    const pick = (entry) => {
        const framework = entry?.payload?.framework ?? entry?.framework;
        if (framework) onSelect(framework);
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="grc-framework-chart">
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <div className="mr-auto min-w-0">
                    <div className="text-sm font-semibold text-gray-800">Framework readiness</div>
                    <div className="text-xs text-gray-500">Click a bar, or choose a framework, to see its requirements.</div>
                </div>
                <label htmlFor="grc-framework-select" className="text-xs text-gray-500">Framework</label>
                <select id="grc-framework-select" data-testid="grc-framework-select" value={selected || ""}
                    onChange={e => onSelect(e.target.value)}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white max-w-full">
                    {(estimates || []).map(e => (
                        <option key={e.framework} value={e.framework}>{optionLabel(e)}</option>
                    ))}
                </select>
            </div>

            {bars.length > 0 && (
                // Clicking a bar must not draw the browser's focus outline around the chart; the drop-down
                // is the keyboard way in, so the chart's own keyboard layer is off.
                <div style={{ width: "100%", height: 300 }} className="[&_*:focus]:outline-none">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={bars} margin={{ top: 22, right: 16, left: 0, bottom: 4 }} accessibilityLayer={false}
                            style={{ outline: "none" }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="framework" type="category" interval={0} angle={-30} textAnchor="end"
                                height={90} tick={{ fontSize: 12 }} />
                            <YAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} width={46} />
                            <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(99, 102, 241, 0.06)" }} />
                            <Bar dataKey="score" barSize={44} minPointSize={3} cursor="pointer" onClick={pick}
                                isAnimationActive={false}>
                                {/* The selected bar is solid, the others faded; no outline on any bar. */}
                                {bars.map(b => (
                                    <Cell key={b.framework} fill={readinessColor(b.score)}
                                        fillOpacity={!selected || b.framework === selected ? 1 : 0.4} stroke="none" />
                                ))}
                                {/* Drawn here so the label never takes the bar's outline or fading (it did: the
                                    selected bar's label came out as a thick blue blob, the others faded). */}
                                <LabelList dataKey="score" content={BarLabel} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
                {LEGEND.map(([color, label]) => (
                    <span key={label} className="flex items-center gap-1"><span className={`w-3 h-3 rounded-full inline-block ${color}`} />{label}</span>
                ))}
            </div>

            {others.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100" data-testid="grc-framework-no-bar">
                    <div className="text-xs text-gray-500 mb-2">No readiness percentage:</div>
                    <div className="flex flex-wrap gap-2">
                        {others.map(e => (
                            <button key={e.framework} type="button" onClick={() => onSelect(e.framework)}
                                data-testid={`grc-framework-chip-${e.framework}`} aria-pressed={e.framework === selected}
                                className={`text-xs px-2.5 py-1 rounded-lg border ${e.framework === selected
                                    ? "border-indigo-500 ring-1 ring-indigo-500 bg-indigo-50 text-indigo-800"
                                    : "border-gray-200 text-gray-700 hover:border-indigo-400"}`}>
                                <span className="font-semibold">{e.framework}</span> · {noBarText(e)}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FrameworkReadinessChart;
